import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function StatusAutomationModal({ onClose, onSuccess }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editHours, setEditHours] = useState('');

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setLoading(true);
    const result = await api.getStatusAutomation();
    if (result.success) {
      setRules(result.data);
    }
    setLoading(false);
  };

  const handleToggle = async (ruleId, currentStatus) => {
    const result = await api.toggleStatusAutomation(ruleId, !currentStatus);
    if (result.success) {
      loadRules();
      if (onSuccess) onSuccess('Automation rule updated successfully!');
    } else {
      alert('Failed to toggle rule');
    }
  };

  const handleUpdateHours = async (ruleId) => {
    if (!editHours || editHours < 1) {
      alert('Please enter valid hours (minimum 1)');
      return;
    }
    const result = await api.updateStatusAutomation(ruleId, parseInt(editHours), null);
    if (result.success) {
      setEditingRule(null);
      setEditHours('');
      loadRules();
      if (onSuccess) onSuccess('Time delay updated successfully!');
    } else {
      alert('Failed to update hours');
    }
  };

  const handleProcessNow = async () => {
    if (!window.confirm('Process all pending status changes now?')) return;
    setProcessing(true);
    const result = await api.processStatusAutomation();
    setProcessing(false);
    if (result.success) {
      if (onSuccess) onSuccess(`Successfully updated ${result.updated_count} orders!`);
      alert(`Success! Updated ${result.updated_count} orders`);
    } else {
      alert('Failed to process: ' + result.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      confirmed: '#8bc34a',
      processing: '#9c27b0',
      packed: '#03a9f4',
      shipped: '#2196f3',
      out_for_delivery: '#00bcd4',
      delivered: '#4caf50'
    };
    return colors[status] || '#666';
  };

  const formatStatusName = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '800px', maxHeight: '90vh', overflow: 'auto'}}>
        <div className="modal-header">
          <div>
            <h2>⚙️ Status Automation Manager</h2>
            <p style={{fontSize: '14px', color: '#666', margin: '5px 0 0 0'}}>Configure automatic order status changes</p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{padding: '24px'}}>
          {/* Info Banner */}
          <div style={{background: '#e3f2fd', padding: '16px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #2196f3'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <span style={{fontSize: '24px'}}>ℹ️</span>
              <div>
                <strong style={{color: '#1976d2'}}>How it works:</strong>
                <p style={{margin: '4px 0 0 0', fontSize: '14px', color: '#555'}}>
                  Orders automatically progress through statuses based on time delays. Enable/disable rules or adjust timing as needed.
                </p>
              </div>
            </div>
          </div>

          {/* Process Now Button */}
          <div style={{marginBottom: '24px', textAlign: 'center'}}>
            <button
              onClick={handleProcessNow}
              disabled={processing}
              style={{
                padding: '12px 32px',
                background: processing ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: processing ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
            >
              {processing ? '⏳ Processing...' : '▶️ Process All Changes Now'}
            </button>
            <p style={{fontSize: '12px', color: '#666', marginTop: '8px'}}>
              Manually trigger status updates for all eligible orders
            </p>
          </div>

          {/* Rules List */}
          {loading ? (
            <div style={{textAlign: 'center', padding: '40px'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>⏳</div>
              <p>Loading automation rules...</p>
            </div>
          ) : rules.length === 0 ? (
            <div style={{textAlign: 'center', padding: '40px'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>📋</div>
              <p>No automation rules found. Please run the SQL setup file.</p>
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              {rules.map((rule, index) => (
                <div
                  key={rule.id}
                  style={{
                    background: rule.is_enabled ? '#fff' : '#f5f5f5',
                    border: `2px solid ${rule.is_enabled ? '#4caf50' : '#ddd'}`,
                    borderRadius: '12px',
                    padding: '20px',
                    transition: 'all 0.3s'
                  }}
                >
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', flex: 1}}>
                      <span style={{fontSize: '24px', fontWeight: '700', color: '#999'}}>#{index + 1}</span>
                      <div style={{display: 'flex', alignItems: 'center', gap: '12px', flex: 1}}>
                        <div style={{
                          padding: '8px 16px',
                          background: getStatusColor(rule.status_from),
                          color: 'white',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          minWidth: '140px',
                          textAlign: 'center'
                        }}>
                          {formatStatusName(rule.status_from)}
                        </div>
                        <span style={{fontSize: '20px', color: '#666'}}>→</span>
                        <div style={{
                          padding: '8px 16px',
                          background: getStatusColor(rule.status_to),
                          color: 'white',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          minWidth: '140px',
                          textAlign: 'center'
                        }}>
                          {formatStatusName(rule.status_to)}
                        </div>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <div
                      onClick={() => handleToggle(rule.id, rule.is_enabled)}
                      style={{
                        width: '60px',
                        height: '30px',
                        background: rule.is_enabled ? '#4caf50' : '#ccc',
                        borderRadius: '15px',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background 0.3s',
                        flexShrink: 0
                      }}
                    >
                      <div style={{
                        width: '26px',
                        height: '26px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: rule.is_enabled ? '32px' : '2px',
                        transition: 'left 0.3s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}></div>
                    </div>
                  </div>

                  {/* Time Delay Section */}
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0'}}>
                    <span style={{fontSize: '14px', color: '#666', fontWeight: '500'}}>⏱️ Time Delay:</span>
                    {editingRule === rule.id ? (
                      <>
                        <input
                          type="number"
                          min="1"
                          value={editHours}
                          onChange={(e) => setEditHours(e.target.value)}
                          placeholder="Hours"
                          style={{
                            width: '80px',
                            padding: '6px 12px',
                            border: '2px solid #2196f3',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                          autoFocus
                        />
                        <span style={{fontSize: '14px', color: '#666'}}>hours</span>
                        <button
                          onClick={() => handleUpdateHours(rule.id)}
                          style={{
                            padding: '6px 16px',
                            background: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ✓ Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingRule(null);
                            setEditHours('');
                          }}
                          style={{
                            padding: '6px 16px',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ✕ Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{
                          padding: '6px 16px',
                          background: '#e3f2fd',
                          color: '#1976d2',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '700'
                        }}>
                          {rule.hours_delay} hours
                        </div>
                        <button
                          onClick={() => {
                            setEditingRule(rule.id);
                            setEditHours(rule.hours_delay);
                          }}
                          style={{
                            padding: '6px 16px',
                            background: '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Edit
                        </button>
                      </>
                    )}
                  </div>

                  {/* Status Indicator */}
                  <div style={{marginTop: '12px', fontSize: '12px', color: rule.is_enabled ? '#4caf50' : '#999', fontWeight: '600'}}>
                    {rule.is_enabled ? '✓ ENABLED - Auto-processing active' : '✕ DISABLED - Manual only'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer Info */}
          <div style={{marginTop: '24px', padding: '16px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ff9800'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <span style={{fontSize: '24px'}}>⚠️</span>
              <div style={{fontSize: '13px', color: '#e65100'}}>
                <strong>Important:</strong> Automation runs every hour via cron job. Manual status changes will reset the timer for that order.
              </div>
            </div>
          </div>

          {/* Close Button */}
          <div style={{marginTop: '24px', textAlign: 'center'}}>
            <button
              onClick={onClose}
              style={{
                padding: '12px 32px',
                background: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatusAutomationModal;